<script setup lang="ts">
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppError } from '@/lib/schema/v2/error'
import axios, { AxiosError } from 'axios'
import { computed } from 'vue'
import { z, ZodError } from 'zod/v4'
import { ampApiErrorSchema } from '@/lib/schema/v2/amp-api-response'

const props = defineProps<{
    error?: AppError
}>()

type LocalError = {
    code?: number
    title: string
    description: string
}

const getAxiosErrorTitle = (error: AxiosError) => {
    const messages = {
        400: 'Invalid request format',
        401: 'Authentication required',
        403: 'Access denied',
        404: 'Resource not found',
        413: 'Request entity too large',
        429: 'Too many requests - please slow down',
        500: 'Internal server error',
        502: 'Service temporarily unavailable',
        503: 'Service maintenance in progress',
    }

    return messages[error.status ?? 0] || error.message
}

const localError = computed<LocalError | undefined | null>(() => {
    if (props.error?.status === 422) {
        return {
            code: 422,
            title: 'Validation error',
            description: 'Please check the form inputs for errors. Scroll up/down if necessary.',
        }
    }

    if (axios.isAxiosError(props.error)) {
        return {
            code: props.error.status,
            title: getAxiosErrorTitle(props.error),
            description: props.error.response?.data?.message,
        }
    }

    if (props.error instanceof ZodError) {
        return {
            title: 'Zod Error',
            description: z.prettifyError(props.error),
        }
    }

    if (Error.isError(props.error)) {
        return props.error?.cause
    }

    const result = ampApiErrorSchema.safeParse(props.error)

    if (result.success) {
        return props.error
    }

    if (
        typeof props.error === 'object' &&
        props.error !== null &&
        Object.keys(props.error).length > 0
    ) {
        return {
            title: 'Validation error',
            description: JSON.stringify(props.error),
        }
    }

    return null
})
</script>

<template>
    <div v-if="localError">
        <Alert variant="error">
            <FontAwesomeIcon icon="fal fa-triangle-exclamation" size="lg" />
            <div>
                <AlertTitle class="text-xs font-semibold">{{ localError?.title }}</AlertTitle>
                <AlertDescription class="whitespace-pre-wrap"
                    >{{ localError?.description }}
                </AlertDescription>
            </div>
        </Alert>
    </div>
</template>

<style scoped></style>
